import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getHomeConfig, type HomeConfig } from '@/lib/api';

interface SiteConfigContextValue {
  config: HomeConfig | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextValue | null>(null);

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<HomeConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      const c = await getHomeConfig();
      setConfig(c);
    } catch {
      setConfig(null);
    }
  }, []);

  useEffect(() => {
    refetch().finally(() => setLoading(false));
  }, [refetch]);

  return (
    <SiteConfigContext.Provider value={{ config, loading, refetch }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error('useSiteConfig must be used within SiteConfigProvider');
  return ctx;
}
